import React from 'react';
import { useSelector } from 'react-redux';

import { LoadingLogo } from '@fmbt/ui/LoadingSpinner';
import FetchFailureModal from 'components/modals/FetchFailureModal';
import { useMyUserMatchesAuth0User } from 'hooks/useMyUserMatchesAuth0User';
import AppScaffoldActionType from 'store/modules/appScaffold/constants';
import { selectAppScaffoldInitialDataLoaded } from 'store/modules/appScaffold/selectors';
import { makeSelectFirstErrorMessage } from 'store/modules/error/selectors';
import MyUserActionType from 'store/modules/myUser/constants';
import { selectIsNetworkReady } from 'store/modules/network/selectors';

const actions = [
  MyUserActionType.LOAD_MY_USER_REQUEST,
  AppScaffoldActionType.LOAD_APP_SCAFFOLD_REQUEST
];

export const selectErrorMessage = makeSelectFirstErrorMessage(actions);

interface Props {
  children: JSX.Element;
}

/**
 * Prevents children from rendering while my user data is loading.
 * Will display an error modal in the event of an error.
 *
 * Note: Authentication is applied by the caller (Routes) so that
 * public routes can bypass the Auth0 redirect.
 */
function ProtectedRoutesLoadingGate({ children }: Props): JSX.Element {
  const myUserMatchesAuth0User = useMyUserMatchesAuth0User();
  const appScaffoldInitialDataLoaded = useSelector(
    selectAppScaffoldInitialDataLoaded
  );
  const isNetworkReady = useSelector(selectIsNetworkReady);
  const errorMessage = useSelector(selectErrorMessage);

  // Waiting for
  // 1) myUser full profile to be fetched and match the signed-in Auth0 user
  // 2) app scaffold data to be loaded from BE or redux
  if (myUserMatchesAuth0User && appScaffoldInitialDataLoaded && isNetworkReady) {
    return children;
  }

  if (errorMessage) {
    return <FetchFailureModal errorMessage={errorMessage} />;
  }

  return <LoadingLogo />;
}

export default ProtectedRoutesLoadingGate;
